import React, { useState, useEffect, useMemo } from 'react'
import styled from '@emotion/styled'
import { Flex } from 'lese'

import { runOnEnter, useInput } from 'Helpers'

import { setSearchParams } from 'Store/encounters'

import { colors } from 'Constants'

import { X } from 'react-feather'

import Link from 'Components/shared/Link'

import AutoSuggest from 'react-autosuggest'
import FuzzySearch from 'fuzzy-search'
import { FancyDatePicker } from './Calendar'

const InputContainer = styled(Flex)`
  background-color: ${({ background }) => background || colors.background800};
  ${({ fullWidth }) => fullWidth && 'width: 100%;'}
`

const Input = styled.input`
  padding: ${({ Icon }) => (Icon ? '12px 4px' : '12px')};
  outline: 0;
  border: none;
  background: transparent;
  font-size: 1em;
  ${({ fullWidth }) => fullWidth && 'width: 100%;'}
`

export default ({ Icon, background, fullWidth, ...props }) => (
  <InputContainer xAlign="flex-start" yAlign background={background} fullWidth={fullWidth}>
    {Icon && <Icon style={{ padding: '12px' }} size="16px" />}
    <Input {...props} fullWidth={fullWidth} />
  </InputContainer>
)

const AutoSuggestStyled = styled.div`
  > div {
    display: flex;
    flex-direction: column;
    position: relative;
  }
  input {
    box-sizing: border-box;
    background-color: ${colors.background800};
    color: ${colors.text};
    border: none;
    outline: none;
    padding: 12px;
    width: 100%;
  }

  > div > div:empty {
    display: none;
  }

  > div > div {
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    background: #fff;
    z-index: 50;
    border: 1px solid ${colors.borderColor};
    border-top: none;
    max-height: 400px;
    overflow-y: auto;

    > ul {
      background: ${colors.background600};
    }

    > ul > li {
      > button {
        text-align: left;
        border: none;
        margin: none;
        outline: none;
        background-color: transparent;
        cursor: pointer;
        padding: 12px 16px;
        width: 100%;
      }

      &:hover,
      &:focus,
      &.react-autosuggest__suggestion--highlighted {
        background: ${colors.background800};
      }
    }
  }
`

export const AutoComplete = ({ suggestions, value, placeholder, onChange, onBlur, ...props }) => {
  const [activeSuggestions, setActiveSuggestions] = useState([])
  const searcher = useMemo(() => new FuzzySearch(suggestions, []), [suggestions])

  useEffect(() => {
    if (value === '' && Array.isArray(suggestions)) setActiveSuggestions(suggestions)
  }, [value, suggestions])
  return (
    <AutoSuggestStyled>
      <AutoSuggest
        suggestions={activeSuggestions}
        renderSuggestion={suggestion => (
          <button
            type="button"
            onClick={() => onChange(suggestion)}
            onKeyDown={runOnEnter(() => onChange(suggestion))}
          >
            {suggestion}
          </button>
        )}
        inputProps={{
          value,
          onChange: (e, { newValue }) => typeof onChange === 'function' && onChange(newValue),
          onBlur: (e, { newValue }) => typeof onBlur === 'function' && onBlur(newValue),
          placeholder,
          ...props
        }}
        shouldRenderSuggestions={val => suggestions.indexOf(val) === -1}
        getSuggestionValue={val => val}
        onSuggestionsClearRequested={() => setActiveSuggestions([])}
        onSuggestionsFetchRequested={args =>
          setActiveSuggestions(
            searcher
              .search(args.value)
              .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
          )
        }
      />
    </AutoSuggestStyled>
  )
}

const AutoCompleteSearchStyled = styled.div`
  width: 100%;
  }
  > div {
    width: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
  }
  input {
    box-sizing: border-box;
    background-color: ${colors.background};
    color: ${colors.text};
    border: none;
    outline: none;
    padding: 12px;
    width: 100%;
  }

  > div > div:empty {
    display: none;
  }

  > div > div {
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    background: #fff;
    z-index: 50;
    border: 1px solid ${colors.borderColor};
    border-top: none;
    max-height: 400px;
    overflow-y: auto;

    > ul {
      background: ${colors.background};
    }

    > ul > li {
      > button {
        text-align: left;
        border: none;
        margin: none;
        outline: none;
        background-color: transparent;
        cursor: pointer;
        padding: 12px 16px;
        width: 100%;
      }

      &:hover,
      &:focus,
      &.react-autosuggest__suggestion--highlighted {
        background: ${colors.background800};
      }
    }
  }
`

export const AutoCompleteSearch = ({
  suggestions,
  value,
  placeholder,
  onChange,
  onBlur,
  ...props
}) => {
  const [activeSuggestions, setActiveSuggestions] = useState([])
  const searcher = useMemo(() => new FuzzySearch(suggestions, []), [suggestions])

  useEffect(() => {
    if (value === '' && Array.isArray(suggestions)) setActiveSuggestions(suggestions)
  }, [value, suggestions])
  return (
    <AutoCompleteSearchStyled>
      <AutoSuggest
        suggestions={activeSuggestions}
        renderSuggestion={suggestion => (
          <button
            type="button"
            onClick={() => onChange(suggestion)}
            onKeyDown={runOnEnter(() => onChange(suggestion))}
          >
            {suggestion}
          </button>
        )}
        inputProps={{
          value,
          onChange: (e, { newValue }) => typeof onChange === 'function' && onChange(newValue),
          onBlur: (e, { newValue }) => typeof onBlur === 'function' && onBlur(newValue),
          placeholder,
          ...props
        }}
        shouldRenderSuggestions={val => suggestions.indexOf(val) === -1}
        getSuggestionValue={val => val}
        onSuggestionsClearRequested={() => setActiveSuggestions([])}
        onSuggestionsFetchRequested={args =>
          setActiveSuggestions(
            searcher
              .search(args.value)
              .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
          )
        }
      />
    </AutoCompleteSearchStyled>
  )
}

const TagStyle = styled(Flex)`
  padding: 12px;
  text-align: center;
  background-color: ${({ backgroundColor }) => backgroundColor || colors.secondary};
  color: ${({ color }) => color || colors.textOnPrimary};
  margin: 4px;
`

const Tag = ({ onClick, children, excludeClose, ...props }) => {
  return (
    <TagStyle {...props} yAlign separation="12px">
      {typeof children === 'string' ? <span>{children}</span> : children}
      {!excludeClose && (
        <Link color={colors.textOnPrimary} onClick={onClick} tabIndex={-1}>
          <X size="16px" />
        </Link>
      )}
    </TagStyle>
  )
}

export const Tags = ({ tags, removeTag, excludeClose }) => {
  return (
    <Flex wrap yAlign>
      {tags.map((tag, i) => (
        <Tag excludeClose={excludeClose} key={tag.text} onClick={() => removeTag(i)}>
          {tag.text}
        </Tag>
      ))}
    </Flex>
  )
}

export const AutoCompleteTags = ({ tags, removeTag, ...props }) => {
  return (
    <Flex column separation="8px" xAlign>
      <AutoComplete {...props} />
      {tags.length > 0 && <Tags tags={tags} removeTag={removeTag} />}
    </Flex>
  )
}

const LabelStyle = styled(Flex)`
  padding: 12px;
  text-align: center;
  background-color: ${({ backgroundColor }) => backgroundColor || colors.secondary};
  color: ${({ color }) => color || colors.textOnPrimary};
  margin: 4px;
`

const Label = ({ excludeClose, form, text, onClick, ...props }) => {
  const [pickedDate, setPickedDate] = useState(new Date())
  const labelHook = useInput('')
  const dateRegex = new RegExp('Date$')
  return (
    <LabelStyle {...props} yAlign separation="12px">
      {form ? (
        <Flex yAlign xAlign style={{ color: colors.textOnPrimary, leftPadding: '2px' }}>
          <span style={{ marginRight: '0.75em' }}>{text}</span>
          {/* TODO: Check if the text is a date so prompt for calendar */}
          {dateRegex.test(text) ? (
            <FancyDatePicker
              onChange={date => {
                setPickedDate(date)
                if (!date) return
                setSearchParams({ keys: [text], values: [date.toISOString()] })
              }}
              date={pickedDate}
            />
          ) : (
            <Input
              style={{
                color: colors.textOnPrimary,
                padding: '6px',
                border: 'none',
                borderBottom: `1px solid ${colors.textOnPrimary}`,
                width: '3em',
                textAlign: 'center'
              }}
              onKeyDown={runOnEnter(() =>
                setSearchParams({ keys: [text], values: [labelHook.value] })
              )}
              {...labelHook.bind}
            />
          )}
        </Flex>
      ) : (
        text
      )}
      {!excludeClose && (
        <Link color={colors.textOnPrimary} onClick={onClick} tabIndex={-1}>
          <X size="0.75em" />
        </Link>
      )}
    </LabelStyle>
  )
}

export const Labels = ({ labels, form, excludeClose, removeLabel }) => {
  return (
    <Flex wrap yAlign>
      {labels.map((label, i) => (
        <Label
          excludeClose={excludeClose}
          key={label.text}
          onClick={() => removeLabel(i)}
          form={form}
          text={label.text}
        />
      ))}
    </Flex>
  )
}

export const AutoCompleteLabels = ({ labels, form, removeLabel, ...props }) => {
  return (
    <Flex column separation="8px" xAlign yAlign>
      <AutoCompleteSearch {...props} />
      {labels.length > 0 && <Labels form={form} labels={labels} removeLabel={removeLabel} />}
    </Flex>
  )
}
